import { component } from 'picoapp';
import Drawer from 'utils/drawer';
import Dropdown from 'utils/dropdown';
import fetchSection from 'utils/fetch-section';

export default component(async (node) => {
  const mobileFilterDrawer: HTMLElement = node.querySelector('[data-mobile-filter-drawer]');
  const mobileFilterDrawerBtn = node.querySelector('[data-mobile-filter-drawer-btn]');
  const mobileFilterDrawerCloseBtns = node.querySelectorAll('[data-mobile-filter-drawer-close-btn]');
  const resultCounts = node.querySelectorAll('[data-result-count]');
  const mobileFilters = node.querySelectorAll<HTMLElement>('[data-mobile-filter]');
  const mobileFilterBtns = node.querySelectorAll<HTMLElement>('[data-mobile-filter-btn]');
  const mobileFilterCloseBtns = node.querySelectorAll<HTMLElement>('[data-mobile-filter-close-btn]');
  const filterDropdownElements = node.querySelectorAll<HTMLElement>('[data-filter-dropdown]');
  const filterDropdownBtns = node.querySelectorAll<HTMLElement>('[data-filter-dropdown-btn]');
  const filterDropdownCloseBtns = node.querySelectorAll<HTMLElement>('[data-filter-dropdown-close-btn]');
  const filterGroups = node.querySelectorAll<HTMLFormElement>('[data-filter-group]');
  const clearFilterBtns = node.querySelectorAll<HTMLElement>('[data-clear-filter-btn]');
  const clearAllBtns = node.querySelectorAll<HTMLElement>('[data-clear-all-btn]');
  const filterValueResets = node.querySelector('[data-filter-value-resets]');
  const productCounts = node.querySelectorAll('[data-product-count]');
  const productOutput = node.querySelector('[data-product-output]');

  interface CollectionConfig {
    sectionId: string,
  }

  interface ProductListConfig {
    pages: number,
  }

  const collectionConfig: CollectionConfig = JSON.parse(node.dataset.config);
  const { sectionId } = collectionConfig;

  const initMobileFilter = () => {
    const drawer = Drawer(mobileFilterDrawer);

    mobileFilterDrawerBtn.addEventListener('click', () => {
      drawer.open();
    });

    mobileFilterDrawerCloseBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        drawer.close();
      });
    });

    mobileFilterCloseBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        drawer.close();
      });
    });

    drawer.on('closed', () => {
      mobileFilters.forEach((filter) => {
        filter.hidden = true;
      });
    });

    mobileFilters.forEach((filter) => {
      const { index } = filter.dataset;

      const filterBtn = Array.from(mobileFilterBtns).find((btn) => (
        btn.dataset.index === index
      ));

      const backBtn = filter.querySelector('[data-mobile-filter-back-btn]');

      filterBtn.addEventListener('click', () => {
        filter.hidden = false;
      });

      backBtn.addEventListener('click', () => {
        filter.hidden = true;
      });
    });
  };

  const initFilterDropdowns = () => {
    filterDropdownElements.forEach((element) => {
      const dropdown = Dropdown(element);
      const { index } = element.dataset;

      const dropdownBtn = Array.from(filterDropdownBtns).find((btn) => (
        btn.dataset.index === index
      ));

      dropdownBtn.addEventListener('click', () => {
        dropdown.open();
      });

      const dropdownCloseBtns = element.querySelectorAll('[data-filter-dropdown-close-btn]');

      dropdownCloseBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          dropdown.close();
        });
      });
    });
  };

  const initPagination = () => {
    const productList = node.querySelector('[data-product-list]');
    const viewPrevBtn = node.querySelector('[data-view-prev-btn]');
    const viewMoreBtn = node.querySelector<HTMLAnchorElement>('[data-view-more-btn]');

    const loadPreviousPages = async () => {
      const currentUrl = new URL(window.location.href);
      const currentPage = parseInt(currentUrl.searchParams.get('page'), 10);

      const fetches = [];

      for (let page = 1; page < currentPage; page += 1) {
        currentUrl.searchParams.set('page', page.toString());
        fetches.push(fetchSection(currentUrl, sectionId, false));
      }

      const html = (await Promise.all(fetches)).map((container) => (
        container.querySelector('[data-product-list]')
      )).map((list) => (
        list.innerHTML
      )).join('');

      productList.insertAdjacentHTML('afterbegin', html);

      viewPrevBtn.remove();
    };

    const loadNextPage = async () => {
      let currentUrl = new URL(window.location.href);
      const nextPage = parseInt(currentUrl.searchParams.get('page') || '1', 10) + 1;

      currentUrl.searchParams.set('page', nextPage.toString());

      const container = await fetchSection(currentUrl, sectionId, true);
      const containerList = container.querySelector<HTMLElement>('[data-product-list]');
      const html = containerList.innerHTML;

      productList.insertAdjacentHTML('beforeend', html);

      const paginationInfo = node.querySelector('[data-pagination-info]');
      const containerPaginationInfo = container.querySelector('[data-pagination-info]');
      paginationInfo.innerHTML = containerPaginationInfo.innerHTML;

      const productListConfig: ProductListConfig = JSON.parse(containerList.dataset.config);
      const { pages } = productListConfig;

      if (nextPage >= pages) {
        viewMoreBtn.remove();
      } else {
        currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('page', (nextPage + 1).toString());
        viewMoreBtn.href = currentUrl.toString();
      }
    };

    viewPrevBtn?.addEventListener('click', loadPreviousPages);
    viewMoreBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      loadNextPage();
    });
  };

  const clearInput = (input: HTMLInputElement) => {
    switch (input.type) {
      case 'checkbox':
        input.checked = false;
        break;

      case 'range':
        input.value = input.dataset.defaultValue;
        break;

      default:
        input.checked = false;
        break;
    }
  };

  const initFilters = () => {
    const updateContent = (container: HTMLElement) => {
      // update count for each filter item
      const containerGroups = container.querySelectorAll('[data-filter-group]');
      containerGroups.forEach((group, groupIndex) => {
        const filters = group.querySelectorAll('[data-filter]');
        const matchingGroup = filterGroups[groupIndex];
        const matchingFilters = matchingGroup.querySelectorAll('[data-filter]');

        filters.forEach((filter, filterIndex) => {
          matchingFilters[filterIndex].innerHTML = filter.innerHTML;
        });
      });

      // update result count in buttons
      const containerResultCount = container.querySelector('[data-result-count'); // we pick the first one, because text content is the same in all
      resultCounts.forEach((count) => {
        count.textContent = containerResultCount.textContent;
      });

      // update reset elements
      const containerValueResets = container.querySelector('[data-filter-value-resets]');
      filterValueResets.innerHTML = containerValueResets.innerHTML;

      // update product counts
      const containerProductCount = container.querySelector('[data-product-count]');
      productCounts.forEach((count) => {
        count.textContent = containerProductCount.textContent;
      });

      // update product list
      const containerProductOutput = container.querySelector('[data-product-output]');
      productOutput.innerHTML = containerProductOutput.innerHTML;

      // update various elements
      const updateElementsByIndices = (
        elementsToUpdate: NodeListOf<HTMLElement>,
        selector: string,
      ) => {
        const containerElements = container.querySelectorAll<HTMLElement>(`${selector}`);

        containerElements.forEach((containerElement) => {
          const { index } = containerElement.dataset;

          const matchingElement = Array.from(elementsToUpdate).find((element) => (
            element.dataset.index === index
          ));

          matchingElement.innerHTML = containerElement.innerHTML;
        });
      };

      updateElementsByIndices(mobileFilterBtns, '[data-mobile-filter-btn]');
      updateElementsByIndices(filterDropdownBtns, '[data-filter-dropdown-btn]');
      updateElementsByIndices(filterDropdownCloseBtns, '[data-filter-dropdown-close-btn]');
    };

    const toggleFilters = () => {
      filterGroups.forEach((group) => {
        group.classList.toggle('collection__filter-group--disabled');
      });

      filterValueResets.classList.toggle('collection__filter-value-resets--disabled');
    };

    const handleChange = async (event: Event) => {
      toggleFilters();

      const form = event.currentTarget as HTMLFormElement;
      const formData = new FormData(form);

      const url = new URL(`${window.location.origin}${window.location.pathname}`);

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Array.from(formData.entries())) {
        // don't include price and sorting if default values
        if (key.includes('price') || key === 'sort_by') {
          const input = form.querySelector<HTMLInputElement>(`[name="${key}"]`);

          if (input.value === input.dataset.defaultValue) {
            // eslint-disable-next-line no-continue
            continue;
          }
        }

        url.searchParams.append(key, value.toString());
      }

      // if filters applied, only show available matches
      if (Array.from(url.searchParams).find(([key]) => !key.includes('price') && key !== 'sort_by')) {
        url.searchParams.append('filter.v.availability', '1');
      }

      const container = await fetchSection(url, sectionId, true);

      updateContent(container);
      initPagination();

      document.dispatchEvent(new Event('app:mount'));

      toggleFilters();
    };

    const removeValue = (event: CustomEvent) => {
      const { detail: { paramName, value } } = event;
      const inputs = filterGroups[0].querySelectorAll('input');

      const matchingInput = Array.from(inputs).find((input) => (
        input.name === paramName && input.value === value
      ));

      clearInput(matchingInput);
      filterGroups[0].dispatchEvent(new Event('change'));
    };

    const clearAllFilters = () => {
      const filters = filterGroups[0].querySelectorAll('[data-filter]');

      filters.forEach((filter) => {
        const inputs = filter.querySelectorAll('input');

        inputs.forEach((input) => {
          clearInput(input);
        });
      });

      filterGroups[0].dispatchEvent(new Event('change'));
    };

    filterGroups.forEach((group) => {
      group.addEventListener('change', handleChange);
    });

    document.addEventListener('filterValue:remove', removeValue);
    document.addEventListener('filterValues:clearAll', clearAllFilters);
  };

  const initClearFiltersBtns = () => {
    clearFilterBtns.forEach((btn) => {
      const { index } = btn.dataset;
      const filterGroup = btn.closest('[data-filter-group]');

      const matchingFilter = Array.from(filterGroup.querySelectorAll<HTMLElement>('[data-filter]')).find((filter) => (
        filter.dataset.index === index
      ));

      btn.addEventListener('click', () => {
        const inputs = matchingFilter.querySelectorAll('input');

        inputs.forEach((input, inputIndex) => {
          clearInput(input);

          if (inputIndex === inputs.length - 1) {
            input.dispatchEvent(new Event('change'));
          }
        });

        filterGroup.dispatchEvent(new Event('change'));
      });
    });
  };

  const initClearAllBtns = () => {
    clearAllBtns.forEach((btn) => {
      if (btn.dataset.initialized === 'true') {
        return;
      }

      btn.addEventListener('click', () => {
        document.dispatchEvent(new Event('filterValues:clearAll'));
      });

      btn.dataset.initialized = 'true';
    });
  };

  const init = () => {
    initMobileFilter();
    initFilterDropdowns();
    initPagination();
    initFilters();
    initClearFiltersBtns();
    initClearAllBtns();
  };

  init();
});
